import React, { useRef, useState } from 'react';
import { IonModal, IonContent, IonButton, IonCheckbox } from '@ionic/react';
import { useSuppressibleNotices } from '@/contexts/SuppressibleNoticesContext';
import {
  INTERSTITIAL_BREAKPOINT,
  INTERSTITIAL_BREAKPOINTS,
  pxToRem,
} from '@/modules/config';
import '@/components/Interstitial.css';

interface NotificationsInterstitialProps {
  isOpen: boolean;
  onDismiss: () => void;
}

const NotificationsInterstitial: React.FC<NotificationsInterstitialProps> = ({
  isOpen,
  onDismiss,
}) => {
  const { suppress, unsuppress } = useSuppressibleNotices();
  const modalRef = useRef<HTMLIonModalElement>(null);
  const contentRef = useRef<HTMLIonContentElement>(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [permissionRequested, setPermissionRequested] = useState(false);

  const expandToAnchor = async (anchorId: string) => {
    await modalRef.current?.setCurrentBreakpoint(1);
    setTimeout(() => {
      const el = document.getElementById(anchorId);
      if (el) {
        contentRef.current?.scrollToPoint(0, el.offsetTop - 16, 300);
      }
    }, 300);
  };

  const handleDismiss = () => {
    onDismiss();
  };

  const handleCheckboxChange = (checked: boolean) => {
    setDontShowAgain(checked);
    if (checked) suppress('notifications');
    else unsuppress('notifications');
  };

  const handleRequestPermission = async () => {
    if (typeof Notification !== 'undefined') {
      await Notification.requestPermission();
    }
    setPermissionRequested(true);
  };

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      initialBreakpoint={INTERSTITIAL_BREAKPOINT}
      breakpoints={INTERSTITIAL_BREAKPOINTS}
      handle
      onDidDismiss={handleDismiss}
    >
      <IonContent ref={contentRef} className="ion-padding-top">
        <p className="interstitial__message">
          {permissionRequested ? (
            <span style={{ color: 'var(--ion-color-success)' }}>
              ✓ Notification permission handled
            </span>
          ) : (
            <>
              Click{' '}
              <button
                className="interstitial__link"
                onClick={handleRequestPermission}
              >
                here
              </button>{' '}
              to enable Notifications so we can help step you through multiple
              trips to your Messaging app.
            </>
          )}{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            (
            <button
              className="interstitial__link"
              onClick={() => expandToAnchor('notif-convince')}
            >
              convince me
            </button>
            )
          </span>
        </p>

        <div className="interstitial__checkbox-row">
          <IonCheckbox
            checked={dontShowAgain}
            onIonChange={e => handleCheckboxChange(e.detail.checked)}
          />
          <span
            style={{ fontSize: pxToRem(14), color: 'var(--ion-color-medium)' }}
          >
            Got it!
          </span>
        </div>

        <IonButton
          expand="block"
          className="interstitial__continue-btn"
          onClick={handleDismiss}
        >
          Continue
        </IonButton>

        {/* Detail zone */}
        <hr className="interstitial__section-rule" />

        <div id="notif-convince">
          <p className="interstitial__section-heading">
            Why enable notifications?
          </p>
          <p className="interstitial__body">
            When you send to multiple recipients, Snowball will walk you through
            a series of interactions with your Messaging app. Without
            notifications, you&apos;ll have to remember to come back to our app
            to get the next step in that walk. With notifications, Snowball taps
            you on the shoulder after each send so you don&apos;t lose your
            place—and we can properly record who was messaged and who
            wasn&apos;t.
          </p>
        </div>

        <div style={{ height: 40 }} />
      </IonContent>
    </IonModal>
  );
};

export default NotificationsInterstitial;
